import { ReactNode, memo } from "react";
import { MiButton } from "@miplatform/components";
import { DATE_TIME_FORMATS } from "@miplatform/constants";
import { ButtonVariants, Colors } from "@miplatform/types";
import { formatDate } from "@miplatform/utils";
import { Box, Typography } from "@mui/material";

export interface MiEmailInfoCardProps {
  title: ReactNode;
  lastSent?: string;
  isActionDisabled?: boolean;
  isViewDisabled?: boolean;
  actionTitle?: string;
  "data-testid"?: string;
  onViewClick?: () => void;
  onActionClick?: () => void;
}

export const MiEmailInfoCard = memo(
  ({
    title,
    lastSent,
    isActionDisabled,
    isViewDisabled,
    actionTitle = lastSent ? "Resend" : "Send",
    "data-testid": dataTestId,
    onViewClick,
    onActionClick,
  }: MiEmailInfoCardProps) => {
    const { cardTestId, chipTestId, timestampTestId } = (() => {
      const type =
        typeof title === "string"
          ? `${title.toLowerCase().replace(/\s+/g, "-")}`
          : undefined;

      return {
        cardTestId: type && !dataTestId ? `mi-email-card-${type}` : dataTestId,
        chipTestId: type ? `mi-email-card-${type}-status-chip` : undefined,
        timestampTestId: type ? `mi-email-card-${type}-timestamp` : undefined,
      };
    })();

    return (
      <Box
        data-testid={cardTestId}
        bgcolor="grey.200"
        border="0.5px solid grey.400"
        borderRadius="2px"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        p={2}
      >
        <Box display="flex" flexDirection="column" gap={1}>
          <Box display="flex" gap={1}>
            <Typography color="bluegrey.dark" fontWeight="600" fontSize={12}>
              {title}
            </Typography>
            <Box
              data-testid={chipTestId}
              border="1px solid"
              bgcolor={lastSent ? "green.main" : "grey.400"}
              borderColor={lastSent ? "green.dark" : "grey.500"}
              borderRadius="2px"
              px={1}
            >
              <Typography color="common.white" fontSize={11}>
                {lastSent ? "Sent" : "Not Sent"}
              </Typography>
            </Box>
          </Box>
          <Typography
            data-testid={timestampTestId}
            color="bluegrey.dark"
            fontSize={11}
          >
            {`Last Sent: ${
              lastSent
                ? ` ${formatDate(
                    lastSent,
                    DATE_TIME_FORMATS.FORMAT_MM_DD_YY
                  )} at ${formatDate(
                    lastSent,
                    DATE_TIME_FORMATS.HOURS_MINUTES
                  )}`
                : "##/##/##"
            }`}
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <MiButton
            title="View"
            variant={ButtonVariants.Outlined}
            color={Colors.Blue}
            size="small"
            sx={{ width: 55 }}
            disabled={isViewDisabled}
            onClick={onViewClick}
          />
          <MiButton
            title={actionTitle}
            variant={ButtonVariants.Contained}
            color={lastSent ? Colors.Orange : Colors.Primary}
            size="small"
            sx={{ width: 65 }}
            disabled={isActionDisabled}
            onClick={onActionClick}
          />
        </Box>
      </Box>
    );
  }
);
